import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import moment from 'moment';

import { useMutation } from '@apollo/client';

import PropTypes from 'prop-types';

import {
  MediaContext, RichTextEditor, convertToRichText, convertToPlainText,
} from '@playbookux/playbook-web-core';
import {
  CREATE_TEST_SESSION_NOTE,
  UPDATE_TEST_SESSION_NOTE,
  updateNoteCreated,
} from '../../../../core/components/video-tab/graphql';

import ModalWithScrubbers from '../../../../core/components/video-player/modal-with-scrubbers';
import {
  Header,
  Icon,
  Form,
  SaveButton,
  Separator,
  Time,
  Title,
  ContainerArrows,
  ContainerInput,
} from './styles';

import MaskedInput from './masked-input/index';
import {
  ERROR_MESSAGE_TIME_DURATION,
  ERROR_MESSAGE_TIME_FINAL,
  getMask,
  getFormat,
  MESSAGE_SAVE,
  MINIMUM_TIME,
  DEFAULT_CLIP_DURATION,
} from './constants';

const ClipModal = (props) => {
  const {
    note,
    testSessionId,
    companyMembers,
    isOpen,
    onClose,
    editorState,
    onChange,
  } = props;

  const { media } = useContext(MediaContext);
  const {
    currentTime,
    getDuration,
  } = media;

  const [startTime, setStartTime] = useState({ seconds: 0, display: '00:00' });
  const [endTime, setEndTime] = useState({ seconds: 5, display: '00:05' });
  const [error, setError] = useState(null);

  const duration = getDuration();
  const mask = getMask(duration);
  const format = getFormat(duration);

  const [createTestSessionNote] = useMutation(CREATE_TEST_SESSION_NOTE, {
    update: updateNoteCreated(testSessionId),
  });
  const [updateTestSessionNote] = useMutation(UPDATE_TEST_SESSION_NOTE);

  const toFormat = useCallback((value) => moment.utc(
    moment.duration(value, 'seconds').asMilliseconds(),
  ).format(format), [format]);

  useEffect(() => {
    if (note) {
      setStartTime({ seconds: note.videoStartTime, display: toFormat(note.videoStartTime) });
      setEndTime({ seconds: note.videoEndTime, display: toFormat(note.videoEndTime) });
      return;
    }
    const end = Math.min(currentTime + DEFAULT_CLIP_DURATION, duration);
    setStartTime({ seconds: currentTime, display: toFormat(currentTime) });
    setEndTime({ seconds: end, display: toFormat(end) });
    // we should not use currentTime as dependency because it will cause bugs
    // when we open the create clip modal and the video is playing
    // also, we don't need to trigger effect everytime currentTime changes
  }, [duration, toFormat, note, isOpen]);

  const handleSaveClip = () => {
    createTestSessionNote({
      variables: {
        testSessionId,
        attributes: {
          note: convertToPlainText(editorState) || '',
          videoStartTime: Math.trunc(startTime.seconds),
          videoEndTime: Math.trunc(endTime.seconds),
          noteType: 'clip',
          noteRichText: JSON.stringify(convertToRichText(editorState)),
        },
      },
    });
  };

  const handleUpdateClip = () => {
    updateTestSessionNote({
      variables: {
        id: note.id,
        attributes: {
          note: convertToPlainText(editorState) || '',
          videoStartTime: Math.trunc(startTime.seconds),
          videoEndTime: Math.trunc(endTime.seconds),
          noteRichText: JSON.stringify(convertToRichText(editorState)),
        },
      },
    });
  };

  const handleSubmit = () => {
    if (!note) {
      handleSaveClip();
    } else {
      handleUpdateClip();
    }

    onClose();
  };

  const handleInitChange = (newValue) => {
    if (newValue < 0) return;
    setStartTime({ seconds: newValue, display: toFormat(newValue) });
  };

  const handleEndChange = (newValue) => {
    if (newValue > duration) return;
    setEndTime({ seconds: newValue, display: toFormat(newValue) });
  };

  useEffect(() => {
    const clipDuration = Math.trunc(endTime.seconds) - Math.trunc(startTime.seconds);
    if (clipDuration < MINIMUM_TIME) {
      return setError(ERROR_MESSAGE_TIME_DURATION);
    }

    if (endTime.seconds > duration) {
      return setError(ERROR_MESSAGE_TIME_FINAL);
    }

    return setError(null);
  }, [startTime.seconds, endTime.seconds, duration]);

  const toSeconds = (value) => moment.duration(
    moment(value, format).diff(moment().startOf('day')),
  ).asSeconds();

  if (!isOpen) {
    return null;
  }

  return (
    <ModalWithScrubbers
      startTime={startTime.seconds}
      endTime={endTime.seconds}
      onStartChange={handleInitChange}
      onEndChange={handleEndChange}
    >
      <Header>
        <Title>Create a clip</Title>
        <Icon name="close" onClick={onClose} />
      </Header>

      <Form>
        <RichTextEditor
          editorState={editorState}
          placeholder="Add a note"
          autoResize
          scrollable
          userSuggestions={companyMembers}
          onChange={onChange}
        />

        <Time>
          <ContainerInput>
            <MaskedInput
              value={startTime.display}
              mask={mask}
              onChange={(value) => setStartTime({ seconds: toSeconds(value), display: value })}
            />
            <ContainerArrows>
              <Icon
                name="angle up"
                onClick={() => handleInitChange(startTime.seconds + 1)}
              />
              <Icon
                name="angle down"
                onClick={() => handleInitChange(startTime.seconds - 1)}
              />
            </ContainerArrows>
          </ContainerInput>

          <Separator>-</Separator>

          <ContainerInput>
            <MaskedInput
              value={endTime.display}
              mask={mask}
              onChange={(value) => setEndTime({ seconds: toSeconds(value), display: value })}
            />
            <ContainerArrows>
              <Icon
                name="angle up"
                onClick={() => handleEndChange(endTime.seconds + 1)}
              />
              <Icon
                name="angle down"
                onClick={() => handleEndChange(endTime.seconds - 1)}
              />
            </ContainerArrows>
          </ContainerInput>
        </Time>

        <SaveButton
          onClick={handleSubmit}
          error={error}
          disabled={error}
        >
          {error || MESSAGE_SAVE}
        </SaveButton>
      </Form>
    </ModalWithScrubbers>
  );
};

ClipModal.propTypes = {
  isOpen: PropTypes.bool,
  testSessionId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  editorState: PropTypes.shape().isRequired,
  companyMembers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onChange: PropTypes.func.isRequired,
  note: PropTypes.shape({
    id: PropTypes.string,
    videoStartTime: PropTypes.number,
    videoEndTime: PropTypes.number,
    note: PropTypes.string,
  }),
};

ClipModal.defaultProps = {
  isOpen: false,
  note: null,
};

export default ClipModal;
